import type { StudentMessages } from "../../state/studentMessages";

const DEFAULT_OVERVIEW =
  "Start writing when you are ready. Your reminders will update here as you work.";
const DEFAULT_NOTICE =
  "Your instructor will see this writing summary under your submission number.";
const DEFAULT_GUIDANCE = "Use this space to keep track of how your draft is developing.";

/**
 * Reference-only prompt area. Its content is deliberately excluded from every
 * writing-process metric, which the muted note states plainly to the student.
 * When the student messages are supplied, the three overview reminders render
 * as compact guidance lines below the input so the live guidance stays next to
 * the prompt instead of in the metrics-only side panel.
 */
export function PromptPanel({
  value,
  onChange,
  messages,
}: {
  value: string;
  onChange: (value: string) => void;
  messages?: StudentMessages;
}) {
  return (
    <section className="prompt-panel" aria-labelledby="assignment-prompt-heading">
      <div className="editor-header">
        <div>
          <p className="editor-label">Assignment prompt</p>
          <p className="prompt-note">Not tracked in writing metadata</p>
          <h3 id="assignment-prompt-heading">Paste your assignment directions here</h3>
        </div>
      </div>
      <label className="sr-only" htmlFor="assignment-prompt-input">
        Assignment prompt
      </label>
      <textarea
        id="assignment-prompt-input"
        className="prompt-input"
        placeholder="Paste the assignment prompt, rubric notes, or teacher directions here for reference. This area is not included in writing-process metrics."
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      {messages && (
        <div className="prompt-guidance" aria-label="Writing reminders" aria-live="polite">
          <p className="guidance-line guidance-warn" data-testid="overview-message">
            {messages.overview || DEFAULT_OVERVIEW}
          </p>
          <p className="guidance-line guidance-data" data-testid="overview-notice">
            {messages.notice || DEFAULT_NOTICE}
          </p>
          <p className="guidance-line guidance-good" data-testid="overview-guidance">
            {messages.guidance || DEFAULT_GUIDANCE}
          </p>
        </div>
      )}
    </section>
  );
}
